/** @jsx jsx */
import React from 'react';
import { styled, css, jsx } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { ErrorFlag } from '@atlassian/jira-issue-error-flag';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useCoreDataFilter, isFilterSuitableForAGSQuery } from '../../../services/core-data';
import {
	useSetViewConfigurationPreference,
	useViewConfigurationPreference,
} from '../../../services/my-preference';
import type {
	IconsViewConfiguration,
	EnvironmentTypeViewConfiguration,
} from '../../../services/my-preference/types';
import EnvironmentTypeConfiguration from './environment-type';
import IconsConfiguration from './icons';
import messages from './messages';

export type Props = {
	onClose: () => void;
};

export const Configuration = ({ onClose }: Props) => {
	const { formatMessage } = useIntl();
	const filter = useCoreDataFilter();
	const {
		loading: queryLoading,
		error: queryError,
		data: remoteConfig,
	} = useViewConfigurationPreference();

	const [setEnvironmentViewPreference, mutationState] = useSetViewConfigurationPreference();

	const showIssuesWithoutDeploymentConfig = !(
		ff('use-ags-for-deployments-timeline_22xgj') &&
		filter &&
		isFilterSuitableForAGSQuery(filter)
	);
	if (queryError) {
		throw new Error(`Could not load view configuration preference. Details: ${queryError.message}`);
	}

	return (
		<>
			{mutationState.error && (
				<ErrorFlag
					error={mutationState.error}
					title={messages.configMutationErrorFlagTitle}
					description={messages.configMutationErrorFlagDescription}
				/>
			)}
			<TitleWrapper>
				<Heading size="medium">
					<div css={titleStyles}>{formatMessage(messages.title)}</div>
				</Heading>
				<Button
					iconBefore={<CrossIcon label="Close" />}
					appearance="subtle"
					onClick={onClose}
					spacing="default"
				/>
			</TitleWrapper>
			{queryLoading || remoteConfig == null ? (
				<Spinner size="small" />
			) : (
				<>
					<div css={bottomMarginStyles}>
						<EnvironmentTypeConfiguration
							onChange={(newEnvironmentTypeViewConfig: EnvironmentTypeViewConfiguration) => {
								setEnvironmentViewPreference({
									...remoteConfig,
									environmentType: newEnvironmentTypeViewConfig,
								});
							}}
							environmentTypeViewConfig={remoteConfig.environmentType}
						/>
					</div>
					<div css={bottomMarginStyles}>
						<IconsConfiguration
							onChange={(iconsViewConfiguration: IconsViewConfiguration) => {
								setEnvironmentViewPreference({
									...remoteConfig,
									icons: iconsViewConfiguration,
								});
							}}
							iconsViewConfiguration={remoteConfig.icons}
						/>
					</div>
					{showIssuesWithoutDeploymentConfig ? (
						<div css={bottomMarginStyles}>
							<Heading size="xsmall">
								<div css={sectionTitleStyles}>{formatMessage(messages.issuesSection)}</div>
							</Heading>
							<Checkbox
								isChecked={remoteConfig.issues.showIssuesWithoutDeployments}
								label={formatMessage(messages.showIssuesWithoutDeployments)}
								onChange={(evt, analyticsEvent: UIAnalyticsEvent) => {
									fireUIAnalytics(analyticsEvent, 'toggleShowIssuesWithoutDeployments', {
										toggleTo: evt.target.checked,
									});
									setEnvironmentViewPreference({
										...remoteConfig,
										issues: {
											showIssuesWithoutDeployments: evt.target.checked,
										},
									});
								}}
							/>
						</div>
					) : (
						<div css={bottomMarginStyles}>
							<Heading size="xsmall">
								<div css={sectionTitleStyles}>{formatMessage(messages.issuesSection)}</div>
							</Heading>
							<Checkbox
								isChecked={false}
								label={formatMessage(messages.showIssuesWithoutDeployments)}
								isDisabled
							/>
							<p>{formatMessage(messages.showIssuesWithoutDeploymentsRemoved)}</p>
						</div>
					)}
				</>
			)}
		</>
	);
};

const bottomMarginStyles = css({
	marginBottom: token('space.300', '24px'),
});

const titleStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	marginTop: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleWrapper = styled.div(
	{
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	bottomMarginStyles,
);

const sectionTitleStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 3}px`,
	marginTop: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginBottom: `${gridSize * 1.25}px`,
});

export default Configuration;
